import React, { useCallback, useRef, useState } from "react";
import { IconButton, Typography } from "@mui/material";
import { TrendingUp, Close, DragIndicator } from "@mui/icons-material";
import styles from "./SalesSummaryOverlay.module.css";

const DEFAULT_STATS = [
  { label: "Today's Sales", value: "1,250.00", trend: "+12.5%" },
  { label: "Transactions", value: "32", trend: "+8.2%" },
  { label: "Avg. Sale", value: "39.06", trend: "+5.7%" },
  { label: "Items Sold", value: "112", trend: "+10.1%" },
];

const SalesSummaryOverlay = ({ stats = DEFAULT_STATS, variant = "floating" }) => {
  const [show, setShow] = useState(true);
  const [pos, setPos] = useState({ x: null, y: null });
  const dragRef = useRef({ dragging: false, offsetX: 0, offsetY: 0 });

  const onDragMove = useCallback((e) => {
    if (!dragRef.current.dragging) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    setPos({
      x: clientX - dragRef.current.offsetX,
      y: clientY - dragRef.current.offsetY,
    });
  }, []);

  const onDragEnd = useCallback(() => {
    dragRef.current.dragging = false;
    document.removeEventListener("mousemove", onDragMove);
    document.removeEventListener("mouseup", onDragEnd);
    document.removeEventListener("touchmove", onDragMove);
    document.removeEventListener("touchend", onDragEnd);
  }, [onDragMove]);

  const onDragStart = (e) => {
    const rect = e.currentTarget.parentElement.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    dragRef.current = {
      dragging: true,
      offsetX: clientX - rect.left,
      offsetY: clientY - rect.top,
    };
    document.addEventListener("mousemove", onDragMove);
    document.addEventListener("mouseup", onDragEnd);
    document.addEventListener("touchmove", onDragMove);
    document.addEventListener("touchend", onDragEnd);
  };

  if (!show) return null;

  if (variant === "inline") {
    const detached = pos.x !== null;
    const inlineStyle = detached
      ? {
          position: "fixed",
          left: `${pos.x}px`,
          top: `${pos.y}px`,
          zIndex: 1120,
        }
      : undefined;

    return (
      <div
        className={`${styles.inlineStats} ${detached ? styles.inlineDetached : ""}`}
        style={inlineStyle}
      >
        <div
          className={styles.inlineDragHandle}
          onMouseDown={onDragStart}
          onTouchStart={onDragStart}
          title="Drag"
        >
          <DragIndicator fontSize="small" />
        </div>
        {stats.map((stat) => (
          <div key={stat.label} className={styles.inlineStatCard}>
            <Typography className={styles.statLabel}>{stat.label}</Typography>
            <div className={styles.statValueRow}>
              <Typography className={styles.statValue}>{stat.value}</Typography>
              <span className={styles.statTrend}>
                <TrendingUp fontSize="inherit" />
                {stat.trend}
              </span>
            </div>
          </div>
        ))}
        {detached && (
          <IconButton
            className={styles.floatingClose}
            onClick={() => setPos({ x: null, y: null })}
            aria-label="Dock stats"
            title="Dock"
          >
            <DragIndicator fontSize="small" />
          </IconButton>
        )}
        <IconButton
          className={styles.floatingClose}
          onClick={() => setShow(false)}
          aria-label="Close stats"
          title="Close"
        >
          <Close fontSize="small" />
        </IconButton>
      </div>
    );
  }

  const floatingStyle =
    pos.x !== null
      ? {
          left: `${pos.x}px`,
          top: `${pos.y}px`,
          right: "auto",
          bottom: "auto",
          transform: "none",
        }
      : undefined;

  return (
    <div className={styles.floatingStats} style={floatingStyle}>
      <div
        className={styles.floatingDragHandle}
        onMouseDown={onDragStart}
        onTouchStart={onDragStart}
      >
        <DragIndicator fontSize="small" />
      </div>
      {stats.map((stat) => (
        <div key={stat.label} className={styles.statCard}>
          <Typography className={styles.statLabel}>{stat.label}</Typography>
          <div className={styles.statValueRow}>
            <Typography className={styles.statValue}>{stat.value}</Typography>
            <span className={styles.statTrend}>
              <TrendingUp fontSize="inherit" />
              {stat.trend}
            </span>
          </div>
        </div>
      ))}
      <IconButton
        className={styles.floatingClose}
        onClick={() => setShow(false)}
        aria-label="Close stats"
      >
        <Close fontSize="small" />
      </IconButton>
    </div>
  );
};

export default SalesSummaryOverlay;
